export type Presence = 'ONLINE' | 'AWAY' | 'IDLE' | 'OFFLINE';
export type TypingState = 'TYPING' | 'TYPED' | 'NONE';

export interface User {
  screenName: string;
  presence: Presence;
  awayMessage: string | null;
  profileText: string | null;
  lastSeen: number;
  createdAt: number;
}

export interface Buddy {
  screenName: string;
  group: string;
  presence: Presence;
  awayMessage: string | null;
  lastSeen: number;
}

export interface Message {
  id: string;
  convoId: string;
  from: string;
  to: string;
  body: string;
  sentAt: number;
}

export interface PresenceEvent {
  screenName: string;
  presence: Presence;
  awayMessage: string | null;
  lastSeen: number;
}

export interface TypingEvent {
  from: string;
  to: string;
  state: TypingState;
}

export interface BuzzEvent {
  from: string;
  to: string;
  at: number;
}

/**
 * A buddy is shown as offline if their heartbeat is older than this, even if
 * their stored presence still says ONLINE — clients that crash never get to
 * write OFFLINE, and the original showed those users dropping off too.
 */
export const PRESENCE_STALE_MS = 90_000;

/** Heartbeat interval. Comfortably under PRESENCE_STALE_MS to tolerate a miss. */
export const PRESENCE_HEARTBEAT_MS = 30_000;

/** Idle after this long without input, matching AIM's default. */
export const IDLE_AFTER_MS = 10 * 60_000;

export function effectivePresence(p: Presence, lastSeen: number, now = Date.now()): Presence {
  if (p === 'OFFLINE') return 'OFFLINE';
  return now - lastSeen > PRESENCE_STALE_MS ? 'OFFLINE' : p;
}

/** Screen names matched case- and space-insensitively on the original service. */
export function normalizeScreenName(name: string): string {
  return name.replace(/\s/g, '').toLowerCase();
}

export function sameScreenName(a: string, b: string): boolean {
  return normalizeScreenName(a) === normalizeScreenName(b);
}
